import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import utc from 'dayjs/plugin/utc';
import { Box } from '@mui/material';
import { toastConfig } from 'config';
import debounce from 'lodash/debounce';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Form, message, Spin } from 'antd';
import SectionCard from 'components/section/SectionCard';
import { useLocation, useNavigate } from 'react-router-dom';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import LeasePageHeader from 'components/header/LeasePageHeader';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LeaseV9NewTrailModeHistory from '../view/LeaseV9NewTrailModeHistory';
import LeaseV9NewTrailModeDetailForm from './LeaseV9NewTrailModeDetailForm';
import LeaseV9NewGeneralLedgerView from '../view/LeaseV9NewGeneralLedgerView';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import LeaseV9NewTrailModeBalanceView from '../view/LeaseV9NewTrailModeBalanceView';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import LeaseV9NewTrailModeScheduleView from '../view/LeaseV9NewTrailModeScheduleView';
import constantFormValues from '../../lease-record-management-v9-new-version/utils/formInputNames';
import {
	stateUpdater,
	utcConverter,
	isSequential,
	isGrossRentLessThanAdvanceOnEachItem,
} from '../../lease-record-management-v9-new-version/utils/helperFunc';

dayjs.extend(utc);

const LeaseV9NewTrailModeModify = ({ screen = 'EDIT' }) => {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const { state } = useLocation();
	const TrialBalanceRef = useRef();
	const actionButtonsRef = useRef();
	const leaseScheduleRef = useRef();
	const leaseDetailsShowAllRef = useRef();

	const [selectedTab, setSelectedTab] = useState(0);
	const [previousFormData, setPreviousFormData] = useState({});
	const [dataFullyLoaded, setDataFullyLoaded] = useState(false);
	const [formState, setFormState] = useState({
		showFormSubmitModal: false,
		submitPayload: null,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed?',
		},
	});
	const [originalValues, setOriginalValues] = useState({
		leaseTerm: null,
		initialDiscountRate: null,
		paymentFrequency: null,
		paymentModality: null,
		grossRents: null,
		advanceAmounts: null,
	});

	const { profileData } = useSelector((state) => state?.profile);

	const [getSingleLeaseData, { data: singleLeaseData, isLoading: isViewLoading }] = useGetSingleIdentityMutation();

	const statusData = useMemo(() => {
		return screen?.toLowerCase() === 'edit'
			? !(
					singleLeaseData?.activationStatus === 'W-I-P' &&
					singleLeaseData?.recordStatus === 'parked' &&
					profileData?.userId === singleLeaseData?.approverUserId[0]
				)
				? singleLeaseData?.data
				: singleLeaseData?.parkedData
			: [];
	}, [singleLeaseData]);

	const [changeRowAdded, setChangeRowAdded] = useState(false);

	const lastProcessedValues = useRef({});

	const [originalChangesLength, setOriginalChangesLength] = useState(0);

	const updateFormState = stateUpdater(setFormState);
	const [rowIndex, setRowIndex] = useState(0);

	const setShowFormSubmitModal = (value) => {
		updateFormState('showFormSubmitModal', value);
	};

	const [updateApi, { isLoading: isUpdateLoading, error: updateError, isSuccess: isUpdateSuccess, isError: isUpdateError }] =
		useGetSingleIdentityMutation();

	useEffect(() => {
		setDataFullyLoaded(false);
		setChangeRowAdded(false);
		lastProcessedValues.current = {};

		getSingleLeaseData({
			suffixCode: '',
			_id: state?._id ?? '',
			operation: 'get-single',
			processCode: state?.processCode,
		});
	}, []);

	useEffect(() => {
		if (isUpdateSuccess) {
			toast.success('Success! Record updated successfully', toastConfig);
			navigate(-2);
		}
		if (isUpdateError) {
			if (updateError?.data?.code === '501') {
				toast.error(updateError?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
		}
	}, [isUpdateSuccess, updateError, isUpdateError]);

	useEffect(() => {
		if (!statusData || Object.keys(statusData).length === 0) return;

		const viewValues = {
			tabIndex: '1',
			modificationSequence: statusData?.modificationSequence,
			leaseId: statusData?.leaseId,
			leaseType: statusData?.leaseType,
			leaseTitle: statusData?.leaseTitle,
			lessorName: statusData?.lessorName,
			paymentFrequency: statusData?.paymentFrequency,
			paymentModality: statusData?.paymentModality,
			creationDate: dayjs(statusData?.creationDateTime?.localDateTimeStringAMPM) ?? null,
			lastModifiedDate: dayjs(statusData?.lastModifiedDate),

			advanceAmount: statusData?.advanceAmount,
			advanceDuration: statusData?.advanceDuration,
			grossRent: statusData?.grossRent,
			leaseTerm: statusData?.leaseTerm,
			leaseStartDate: dayjs(statusData?.leaseStartDate),
			leaseEndDate: dayjs(statusData?.leaseEndDate),
			escalationPercentage: 0,
			initialDiscountRate: statusData?.initialDiscountRate,
			upfrontLeasePayment: statusData?.upfrontLeasePayment,
			initialDirectCost: statusData?.initialDirectCost,
			securityDeposit: statusData?.securityDeposit,
			cashIncentiveAmount: statusData?.cashIncentiveAmount,
			endOfFinancialYear: dayjs(statusData?.endOfFinancialYear),

			gl_rouAssetInitialRecognition: statusData?.gl_rouAssetInitialRecognitionValue,
			gl_rouAssetModification: statusData?.gl_rouAssetModificationValue,
			gl_rouAssetPrepayments: statusData?.gl_rouAssetPrepaymentsValue,
			gl_rouAssetUpfrontPayment: statusData?.gl_rouAssetUpfrontPaymentValue,

			gl_rouAssetIndirectCosts: statusData?.gl_rouAssetIndirectCostsValue,
			gl_rouCashIncentive: statusData?.gl_rouCashIncentiveValue,
			gl_rouAssetKnockOff: statusData?.gl_rouAssetKnockOffValue,
			gl_securityDeposit: statusData?.gl_securityDepositValue,
			gl_leaseLiabilityInitialRecognition: statusData?.gl_leaseLiabilityInitialRecognitionValue,
			gl_leaseLiabilityModification: statusData?.gl_leaseLiabilityModificationValue,
			gl_leaseLiabilityPayment: statusData?.gl_leaseLiabilityPaymentValue,
			gl_leaseLiabilityInterest: statusData?.gl_leaseLiabilityInterestValue,
			gl_interestExpense: statusData?.gl_interestExpenseValue,
			gl_depreciationRou: statusData?.gl_depreciationRouValue,
			gl_accumDepreciationRouDepreciation: statusData?.gl_accumDepreciationRouDepreciationValue,
			gl_accumDepreciationRouKnockOff: statusData?.gl_accumDepreciationRouKnockOffValue,
			gl_apControlLedgerPrepayments: statusData?.gl_apControlLedgerPrepaymentsValue,
			gl_apControlLedgerUpfrontPayment: statusData?.gl_apControlLedgerUpfrontPaymentValue,
			gl_apControlLedgerIndirectCosts: statusData?.gl_apControlLedgerIndirectCostsValue,
			gl_apControlLedgerSecurityDeposit: statusData?.gl_apControlLedgerSecurityDepositValue,
			gl_apControlLedgerPayment: statusData?.gl_apControlLedgerPaymentValue,
			gl_arCashIncentive: statusData?.gl_arCashIncentiveValue,

			version: String(singleLeaseData?.version),
			identityId: singleLeaseData?._id,
			leaseLiability: statusData?.leaseLiability,
			effectiveDateHistory: statusData?.effectiveDateHistory,
			leaseName: statusData?.leaseName,

			month: statusData?.month,

			agreementFiles: statusData?.agreementFiles,

			discountRateChanges: statusData?.discountRateChanges,
			advanceAmounts: statusData?.advanceAmounts,
			grossRents: statusData?.grossRents,
			grossRentList: statusData?.grossRentList,
			grossRentListSet: statusData?.grossRentListSet,
			advanceAmountListSet: statusData?.advanceAmountListSet,
			advanceAmountList: statusData?.advanceAmountList,
			changes: statusData?.changes?.map((item) => {
				const { inputChangeEffectiveDate, ...change } = item;
				return {
					...change,
					inputChangeEffectiveDate: dayjs(inputChangeEffectiveDate),
				};
			}),
		};

		setOriginalChangesLength(statusData?.changes?.length || 0);

		setRowIndex(statusData?.changes?.length);

		form.setFieldsValue(viewValues);

		setTimeout(() => {
			const originalFieldValues = {
				leaseTerm: form.getFieldValue('leaseTerm'),
				initialDiscountRate: form.getFieldValue('initialDiscountRate'),
				paymentFrequency: form.getFieldValue('paymentFrequency'),
				paymentModality: form.getFieldValue('paymentModality'),
				grossRents: form.getFieldValue('grossRents'),
				advanceAmounts: form.getFieldValue('advanceAmounts'),
			};

			lastProcessedValues.current = { ...originalFieldValues };

			setOriginalValues(originalFieldValues);

			setDataFullyLoaded(true);
		}, 500);
	}, [statusData]);

	const [selectHistory, setSelectHistory] = useState(null);

	const manageChangeRows = useCallback(() => {
		if (!dataFullyLoaded) {
			return;
		}

		const currentChangeList = form.getFieldValue('changes') || [];
		const maxAllowedRows = originalChangesLength + 1;

		if (currentChangeList.length > maxAllowedRows) {
			const trimmedList = currentChangeList.slice(0, maxAllowedRows);
			form.setFieldsValue({ changes: trimmedList });

			setRowIndex(maxAllowedRows);
			setChangeRowAdded(maxAllowedRows > originalChangesLength);

			return;
		}

		const currentValues = {
			leaseTerm: form.getFieldValue('leaseTerm'),
			initialDiscountRate: form.getFieldValue('initialDiscountRate'),
			paymentFrequency: form.getFieldValue('paymentFrequency'),
			paymentModality: form.getFieldValue('paymentModality'),
			grossRents: form.getFieldValue('grossRents'),
			advanceAmounts: form.getFieldValue('advanceAmounts'),
		};

		if (isEqual(currentValues, lastProcessedValues.current)) {
			return;
		}

		lastProcessedValues.current = { ...currentValues };

		const hasTermChanged = currentValues.leaseTerm !== originalValues.leaseTerm;
		const hasDiscountRateChanged = currentValues.initialDiscountRate !== originalValues.initialDiscountRate;
		const hasPaymentFrequencyChanged = currentValues.paymentFrequency !== originalValues.paymentFrequency;
		const hasPaymentModalityChanged = currentValues.paymentModality !== originalValues.paymentModality;

		const normalizeArray = (arr) => {
			if (!Array.isArray(arr)) return arr;
			return [...arr].map((v) => (typeof v === 'string' ? Number(v) || 0 : v || 0)).sort((a, b) => a - b);
		};

		const hasGrossRentsChanged = !isEqual(normalizeArray(currentValues.grossRents), normalizeArray(originalValues.grossRents));
		const hasAdvanceAmountsChanged = !isEqual(normalizeArray(currentValues.advanceAmounts), normalizeArray(originalValues.advanceAmounts));

		const hasChanges =
			hasTermChanged ||
			hasDiscountRateChanged ||
			hasPaymentFrequencyChanged ||
			hasPaymentModalityChanged ||
			hasGrossRentsChanged ||
			hasAdvanceAmountsChanged;

		if (!hasChanges && changeRowAdded && currentChangeList.length > originalChangesLength) {
			const updatedList = currentChangeList.slice(0, originalChangesLength);

			form.setFieldsValue({ changes: updatedList });
			setRowIndex(originalChangesLength);
			setChangeRowAdded(false);
			message.info('Changes reverted. The added change record has been removed.');

			return;
		}

		if (!hasChanges) {
			return;
		}

		const hasAutoadedRow = currentChangeList.length > originalChangesLength;
		let shouldCreateNewRow = !hasAutoadedRow;

		if (hasAutoadedRow) {
			const lastRow = currentChangeList[currentChangeList.length - 1];

			if (lastRow && lastRow.inputChangeEffectiveDate) {
				shouldCreateNewRow = false;
			}
		}

		let changeDescription = 'Initial Rent';

		if (hasGrossRentsChanged && hasAdvanceAmountsChanged) {
			changeDescription = 'Change In rent and advance amount';
		} else if (hasGrossRentsChanged) {
			changeDescription = 'Change In rent';
		} else if (hasAdvanceAmountsChanged) {
			changeDescription = 'Change In advance';
		}

		const newRow = {
			paymentFrequency: currentValues.paymentFrequency,
			paymentTime: currentValues.paymentModality,
			inputChangeLeaseTerm: currentValues.leaseTerm,
			inputChangeGrossAmount: 0,
			inputChangeDiscountRate: currentValues.initialDiscountRate,
			inputChangeEscalation: form.getFieldValue('escalationPercentage') || 0,
			inputChangeAdvance: form.getFieldValue('advanceAmount'),
			inputChangeAdvanceAmounts: currentValues.advanceAmounts,
			inputChangeGrossRents: currentValues.grossRents,
			inputChangeDescription: changeDescription,
		};

		if (shouldCreateNewRow) {
			if (currentChangeList.length < maxAllowedRows) {
				newRow.inputChangeEffectiveDate = null;
				const updatedList = [...currentChangeList, newRow];
				form.setFieldsValue({ changes: updatedList });
				setRowIndex(updatedList.length);
				setChangeRowAdded(true);

				requestAnimationFrame(() => {
					const newRowIndex = updatedList.length - 1;
					form.setFields([
						{
							name: ['changes', newRowIndex, 'inputChangeEffectiveDate'],
							errors: ['Effective Date is required'],
							touched: true,
						},
					]);
				});

				message.info('A new change record has been added. Please set the effective date.');
			}
		} else {
			const lastRowIndex = currentChangeList.length - 1;
			const lastRow = currentChangeList[lastRowIndex];

			if (lastRow) {
				newRow.inputChangeEffectiveDate = lastRow.inputChangeEffectiveDate;
				if (lastRow.inputChangeMonth) {
					newRow.inputChangeMonth = lastRow.inputChangeMonth;
				}
			}

			const updatedList = [...currentChangeList];
			updatedList[lastRowIndex] = newRow;
			form.setFieldsValue({ changes: updatedList });

			if (lastRow && lastRow.inputChangeDescription !== changeDescription) {
				message.info(`Change type updated to "${changeDescription}"`);
			}
		}
	}, [form, originalValues, changeRowAdded, dataFullyLoaded, originalChangesLength, lastProcessedValues]);
	const debouncedManageChangeRows = useMemo(() => debounce(manageChangeRows, 300), [manageChangeRows]);

	useEffect(() => {
		if (screen === 'EDIT') {
			window.debouncedManageChangeRows = debouncedManageChangeRows;
		}

		return () => {
			if (window.debouncedManageChangeRows === debouncedManageChangeRows) {
				delete window.debouncedManageChangeRows;
			}
		};
	}, [debouncedManageChangeRows, screen]);

	function compareAndRemoveDuplicates(inputArray) {
		if (!Array.isArray(inputArray) || inputArray.length < 2) {
			return {
				data: inputArray,
				isChanged: false,
			};
		}

		const lastElement = inputArray[inputArray.length - 1];
		const secondLastElement = inputArray[inputArray.length - 2];

		const normalizeForComparison = (arr) => {
			if (!Array.isArray(arr)) return arr;

			return arr.map((item) => {
				const normalizedItem = JSON.parse(JSON.stringify(item));

				return normalizedItem.map((obj) => {
					const result = { ...obj };

					if (obj.amount !== undefined && typeof obj.amount === 'string') {
						const numericValue = parseFloat(obj.amount.replace(/,/g, ''));

						if (!isNaN(numericValue)) {
							result.amount = numericValue.toFixed(2);
						}
					}

					return result;
				});
			});
		};

		const normalizedLast = normalizeForComparison([lastElement])[0];
		const normalizedSecondLast = normalizeForComparison([secondLastElement])[0];

		const areEqual = JSON.stringify(normalizedLast) === JSON.stringify(normalizedSecondLast);

		if (areEqual) {
			const modifiedArray = inputArray.slice(0, -1);

			return {
				data: modifiedArray,
				isChanged: false,
			};
		} else {
			return {
				data: inputArray,
				isChanged: true,
			};
		}
	}
	const handleSubmit = async () => {
		debouncedManageChangeRows();
		const formData = form.getFieldsValue();
		if (formData?.paymentFrequency === 'Quarterly' && formData?.leaseTerm % 3 !== 0) {
			message.error(
				"Total lease months must be a multiple of 3 for quarterly payments. Please correct the Lease Payment Schedule's To Month field"
			);
			return;
		}
		if (!isSequential(formData?.grossRentList)) {
			message.error('Gross Rent List must be sequential');
			return;
		}
		if (!isSequential(formData?.advanceAmountList)) {
			message.error('Advance Amount List must be sequential');
			return;
		}

		const isGrossRentAndAdvanceAmountValid = isGrossRentLessThanAdvanceOnEachItem(formData?.grossRents, formData?.advanceAmounts);
		if (!isGrossRentAndAdvanceAmountValid) {
			message.error('Inconsistency between Lease Payment and Advance Adjustment');
			return;
		}
		let grossRentListSet = formData?.grossRentListSet || [];
		let advanceAmountListSet = formData?.advanceAmountListSet || [];
		let effectiveDateHistory = formData?.effectiveDateHistory || [];
		grossRentListSet?.push(formData?.grossRentList);
		const { data: grossRentListSetData, isChanged: grossRentListSetChanged } = compareAndRemoveDuplicates(grossRentListSet);

		if (grossRentListSetChanged) {
			effectiveDateHistory?.push(utcConverter(formData?.changes?.[formData?.changes?.length - 1]?.inputChangeEffectiveDate));
		}

		const changesPayload = formData.changes?.map((change) => {
			if (change.inputChangeEffectiveDate) {
				return {
					...change,
					inputChangeAdvance: change.inputChangeAdvanceAmounts?.reduce((accumulator, currentValue) => accumulator + currentValue, 0),
					inputChangeEffectiveDate: utcConverter(change.inputChangeEffectiveDate),
					upfrontLeasePayment: formData?.upfrontLeasePayment,
					initialDirectCost: formData?.initialDirectCost,
					securityDeposit: formData?.securityDeposit,
					cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
				};
			}
			return change;
		});
		const editPagePayload = {
			modificationSequence: statusData?.modificationSequence + 1,
			leaseStartDate: formData?.leaseStartDate,
			leaseTitle: formData?.leaseTitle,
			lessorName: formData?.lessorName,
			leaseTerm: formData?.leaseTerm,
			leaseEndDate: formData?.leaseEndDate,
			paymentModality: formData?.paymentModality,
			leaseType: formData?.leaseType,
			initialDiscountRate: formData?.initialDiscountRate,
			grossRentList: formData?.grossRentList,
			paymentFrequency: formData?.paymentFrequency,
			advanceAmountList: formData?.advanceAmountList,
			advanceAmount: formData?.advanceAmount,
			advanceAmounts: formData?.advanceAmounts,
			upfrontLeasePayment: formData?.upfrontLeasePayment,
			initialDirectCost: formData?.initialDirectCost,
			securityDeposit: formData?.securityDeposit,
			grossRents: formData?.grossRents,
			changes: changesPayload,
			modifiedDateTime: utcConverter(dayjs()),
			cashIncentiveAmount: formData?.cashIncentiveAmount,
			paymentModalityLabel: constantFormValues?.paymentModality?.options?.find((item) => item.value === formData?.paymentModality)?.label,
			lastModifiedDate: utcConverter(dayjs()),
			grossRentListSet: grossRentListSetData,
			advanceAmountListSet,
			effectiveDateHistory,
			advanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0).length,
		};

		updateFormState('showFormSubmitModal', true);
		updateFormState('modalData.head', 'Confirmation');
		updateFormState('modalData.label', 'Do you want to update this lease record?');
		updateFormState('submitPayload', editPagePayload);
	};

	const handleModalSubmit = () => {
		updateApi({
			operation: 'update-single',
			processCode: state?.processCode,
			suffixCode: '',
			data: formState?.submitPayload,
			_id: state._id,
			version: +singleLeaseData?.version,
		});
	};
	const hasFormDataChanged = () => {
		const currentFormData = form.getFieldsValue();

		if (screen?.toLowerCase() !== 'create') {
			const currentPayload = {
				advanceAmount: currentFormData?.changes?.[0]?.inputChangeAdvanceAmounts?.reduce((acc, curr) => acc + curr, 0),
				leaseTerm: currentFormData?.changes?.[0]?.inputChangeLeaseTerm,
				discountRate: currentFormData?.changes?.[0]?.inputChangeDiscountRate,
				startDay: String(currentFormData?.leaseStartDate?.date()),
				startMonth: String(currentFormData?.leaseStartDate?.month() + 1),
				startYear: String(currentFormData?.leaseStartDate?.year()),
				paymentFrequency: currentFormData?.changes?.[0]?.paymentFrequency,
				paymentTime: currentFormData?.changes?.[0]?.paymentTime,
				advanceDuration: currentFormData?.changes?.[0]?.inputChangeAdvanceAmounts?.filter((item) => item !== 0)?.length,
				upfrontLeasePayment: currentFormData?.changes?.[0]?.upfrontLeasePayment,
				initialDirectCost: currentFormData?.changes?.[0]?.initialDirectCost,
				securityDeposit: currentFormData?.securityDeposit,
				endOfFinancialYear: currentFormData?.endOfFinancialYear,
				advanceAmounts: currentFormData?.changes?.[0]?.inputChangeAdvanceAmounts,
				grossRents: currentFormData?.changes?.[0]?.inputChangeGrossRents,
			};

			const prevPayload = {
				...previousFormData,
			};

			const currentStr = JSON.stringify(currentPayload);
			const prevStr = JSON.stringify(prevPayload);

			if (currentStr !== prevStr) {
				setPreviousFormData(currentPayload);
				return true;
			}
			return false;
		}

		const currentPayload = {
			advanceAmount: currentFormData?.advanceAmount,
			leaseTerm: currentFormData?.leaseTerm,
			discountRate: currentFormData?.initialDiscountRate,
			startDay: String(currentFormData?.leaseStartDate?.date()),
			startMonth: String(currentFormData?.leaseStartDate?.month() + 1),
			startYear: String(currentFormData?.leaseStartDate?.year()),
			paymentFrequency: currentFormData?.paymentFrequency,
			paymentTime: currentFormData?.paymentModality,
			advanceDuration: currentFormData?.advanceAmounts?.filter((item) => item !== 0)?.length,
			upfrontLeasePayment: currentFormData?.upfrontLeasePayment,
			initialDirectCost: currentFormData?.initialDirectCost,
			securityDeposit: currentFormData?.securityDeposit,
			endOfFinancialYear: currentFormData?.endOfFinancialYear,
			advanceAmounts: currentFormData?.advanceAmounts,
			grossRents: currentFormData?.grossRents,
		};

		const currentStr = JSON.stringify(currentPayload);
		const prevStr = JSON.stringify(previousFormData);

		if (currentStr !== prevStr) {
			setPreviousFormData(currentPayload);

			return true;
		}

		return false;
	};

	const selectTabHandler = (event, newValue) => {
		setSelectedTab(newValue);
		debouncedManageChangeRows();

		if (newValue === 1 && dataFullyLoaded) {
			if (leaseScheduleRef.current?.queryV3Loader) {
				leaseScheduleRef.current.queryV3Loader();
			}
		}

		if (newValue === 3 && dataFullyLoaded) {
			if (leaseDetailsShowAllRef.current?.loadData) {
				leaseDetailsShowAllRef.current.loadData();
			}
		}

		if (newValue === 4 && dataFullyLoaded) {
			if (TrialBalanceRef.current?.loadData) {
				TrialBalanceRef.current.loadData();
			}
		}
	};
	useEffect(() => {
		if (dataFullyLoaded) {
			if (actionButtonsRef.current?.flag?.check !== 0) {
				if (hasFormDataChanged()) {
					actionButtonsRef?.current?.handleDataLoading();
				}
			} else {
				actionButtonsRef?.current?.queryV3Loader();
			}
		}
	}, [dataFullyLoaded, screen]);

	const tabItem = [
		{
			key: '1',
			label: 'Lease Details',
			children: <LeaseV9NewTrailModeDetailForm />,
		},
		{
			key: '2',
			label: 'Lease Schedule',
			children: <LeaseV9NewTrailModeScheduleView viewData={singleLeaseData} dataFullyLoaded={dataFullyLoaded} />,
		},
		{
			key: '3',
			label: 'Modification History',
			children: <LeaseV9NewTrailModeHistory viewData={singleLeaseData} selectHistory={setSelectHistory} />,
		},
		{
			key: '4',
			label: 'General Ledgers',
			children: <LeaseV9NewGeneralLedgerView />,
		},
		{
			key: '5',
			label: 'Trial Balance',
			children: <LeaseV9NewTrailModeBalanceView />,
		},
	];

	const showSpinner = isUpdateLoading || isViewLoading;

	useEffect(() => {
		if (singleLeaseData && singleLeaseData.activationStatus !== undefined) {
			window.leaseActivationStatus = singleLeaseData.activationStatus;
		}
	}, [singleLeaseData]);

	return (
		<Box>
			<Spin spinning={showSpinner} style={{ maxHeight: '100%' }}>
				<LeasePageHeader
					createBtn={false}
					tabsItem={tabItem}
					selectedTab={selectedTab}
					selectTabHandler={selectTabHandler}
					title={`Modify ${state?.processName}`}
					secondaryTitle={screen === 'CREATE' ? '' : `Lease ID: ${statusData?.leaseId || ''}`}
				/>

				<SectionCard>
					<Form form={form} layout="vertical" autoComplete="off" requiredMark={false} onFinish={handleSubmit}>
						<AntdValuePreview form={form} show={false} />

						<>
							{tabItem?.map((item, index) => (
								<Box
									key={index}
									style={{
										display: selectedTab === index ? 'block' : 'none',
									}}
								>
									{item.children}
								</Box>
							))}
						</>
					</Form>
				</SectionCard>

				{formState?.showFormSubmitModal && (
					<ComptrollerConfirmModal
						showModal={formState?.showFormSubmitModal}
						modalData={formState?.modalData}
						setShowModal={setShowFormSubmitModal}
						handleSubmit={handleModalSubmit}
					/>
				)}
				{selectHistory && (
					<IdentityCaseActivityModal
						title={state?.processName}
						setSelectHistory={setSelectHistory}
						selectItem={selectHistory}
						processCode={singleLeaseData?.processCode}
						processVersion={singleLeaseData?.parkProcessVersion}
					/>
				)}
			</Spin>
		</Box>
	);
};

export default LeaseV9NewTrailModeModify;
